import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, X, ChevronLeft, ChevronRight, Plus, Check, MapPin, Building2, Layers, Ruler } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/motion";
import { PROJECTS_UNDER_CONSTRUCTION, PROJECTS_COMPLETED } from "../lib/content";
import { Seo } from "../components/Seo";

const APPROACH = [
  { n: "01", title: "Planning", body: "Careful pre-construction planning establishes a strong foundation for successful execution." },
  { n: "02", title: "Execution", body: "Experienced field teams coordinate every phase with precision and accountability." },
  { n: "03", title: "Quality", body: "Continuous inspections ensure every detail meets our quality standards." },
  { n: "04", title: "Safety", body: "Safety remains our highest priority throughout every stage of construction." },
  { n: "05", title: "Delivery", body: "Projects are completed efficiently while maintaining exceptional craftsmanship." },
];

const TRUST = [
  "Ground-Up Multifamily Specialists",
  "Experienced Project Leadership",
  "Transparent Communication",
  "Safety-First Culture",
  "Superior Craftsmanship",
  "Reliable Schedule Management",
  "Quality-Driven Execution",
];

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="project-lightbox"
      onClick={onClose}
    >
      <button onClick={onClose} data-testid="lightbox-close" className="absolute top-6 right-6 text-white/70 hover:text-gold transition-colors"><X size={30} /></button>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }} data-testid="lightbox-prev" className="absolute left-4 md:left-10 text-white/70 hover:text-gold transition-colors"><ChevronLeft size={40} /></button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }} data-testid="lightbox-next" className="absolute right-4 md:right-10 text-white/70 hover:text-gold transition-colors"><ChevronRight size={40} /></button>
        </>
      )}
      <motion.img
        key={index}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        src={images[index]}
        onClick={(e) => e.stopPropagation()}
        alt="Next Horizon Builders project photograph"
        className="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl"
      />
      {images.length > 1 && (
        <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-[0.2em] uppercase">
          {index + 1} / {images.length}
        </span>
      )}
    </motion.div>
  );
};

const StatusBadge = ({ status }) => {
  const completed = status === "Completed";
  return (
    <span
      data-testid={`project-status-${completed ? "completed" : "active"}`}
      className={`inline-flex items-center gap-2 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${
        completed ? "bg-navy/5 text-navy" : "bg-gold/10 text-gold"
      }`}
    >
      {completed ? (
        <Check size={12} strokeWidth={3} />
      ) : (
        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
      )}
      {status}
    </span>
  );
};

const SpecRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100">
    <Icon size={16} className="text-gold mt-0.5 shrink-0" />
    <div className="flex flex-col">
      <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-darkgray/40">{label}</span>
      <span className="text-sm text-navy font-light mt-0.5">{value}</span>
    </div>
  </div>
);

const ProjectBlock = ({ project, i, onOpen }) => (
  <Reveal>
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
      {/* Featured + thumbs */}
      <div>
        <button
          onClick={() => onOpen(0)}
          data-testid={`project-featured-${i}`}
          className="group relative overflow-hidden aspect-[4/5] w-full block"
        >
          <img src={project.images[0]} alt={project.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
          <div className="absolute inset-0 bg-navy/10 group-hover:bg-navy/0 transition-colors duration-500" />
          <span className="absolute bottom-5 right-5 w-12 h-12 flex items-center justify-center bg-white/90 text-navy opacity-0 group-hover:opacity-100 transition-opacity duration-300"><Plus size={20} /></span>
        </button>
        {project.images.length > 1 && (
          <div className="mt-4 grid grid-cols-4 gap-4">
            {project.images.slice(1).map((img, idx) => (
              <button
                key={img}
                onClick={() => onOpen(idx + 1)}
                data-testid={`project-thumb-${i}-${idx}`}
                className="group relative overflow-hidden aspect-square block"
              >
                <img src={img} alt={`${project.title} construction photo`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        <StatusBadge status={project.status} />
        <h3 className="mt-5 font-serif font-light text-4xl md:text-5xl text-navy tracking-tight">{project.title}</h3>
        <p className="mt-6 text-darkgray/70 text-lg leading-relaxed font-light">{project.description}</p>

        {/* Spec sheet */}
        <div className="mt-8 grid sm:grid-cols-2 gap-x-10">
          <SpecRow icon={MapPin} label="Location" value={project.location} />
          <SpecRow icon={Building2} label="Project Type" value={project.type} />
          <SpecRow icon={Layers} label="Building Height" value={project.stories} />
          <SpecRow icon={Ruler} label="Approx. Size" value={project.size} />
          <SpecRow icon={Building2} label="Residential Units" value={project.units} />
        </div>

        <button
          onClick={() => onOpen(0)}
          data-testid={`project-gallery-${i}`}
          className="group mt-8 inline-flex items-center gap-3 text-navy text-xs font-semibold uppercase tracking-[0.2em]"
        >
          View Gallery
          <ArrowUpRight size={18} className="text-gold group-hover:rotate-45 transition-transform duration-300" />
        </button>
      </div>
    </div>
  </Reveal>
);

export default function Projects() {
  const [box, setBox] = useState(null); // { images, index }

  const open = (images) => (index) => setBox({ images, index });
  const close = useCallback(() => setBox(null), []);
  const prev = useCallback(() => setBox((b) => b && { ...b, index: (b.index - 1 + b.images.length) % b.images.length }), []);
  const next = useCallback(() => setBox((b) => b && { ...b, index: (b.index + 1) % b.images.length }), []);

  return (
    <>
      <Seo
        title="Projects | Next Horizon Builders"
        description="View ground-up multifamily construction projects by Next Horizon Builders across Southern California, from active builds under construction to completed communities."
        path="/projects"
        image="/media/projects/mariposa112-2.jpg"
      />
      <PageHero
        eyebrow="Our Portfolio"
        titleLines={["Our Projects"]}
        tagline="Building Communities Across Southern California"
        subtitle="Explore our multifamily residential developments — from active builds currently under construction to delivered communities. Every project reflects our commitment to quality craftsmanship, disciplined execution, and lasting value for the neighborhoods we help build."
        cta={{ label: "View Our Projects", to: "#under-construction" }}
        image="/media/projects/mariposa112-2.jpg"
        imageAlt="Next Horizon Builders multifamily residential project under construction in Southern California"
      />

      {/* Under Construction */}
      <section id="under-construction" className="bg-white pt-24 md:pt-36 pb-16 md:pb-24 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Now Building</span>
          </div>
          <SectionHeading
            title="Under Construction"
            subtitle="Next Horizon Builders is actively managing ground-up multifamily developments throughout Southern California — each executed with careful planning, experienced field leadership, and an unwavering commitment to safety, quality, and schedule performance."
          />
          <div className="mt-16 md:mt-20 space-y-24 md:space-y-36">
            {PROJECTS_UNDER_CONSTRUCTION.map((p, i) => (
              <ProjectBlock key={p.title} project={p} i={i} onOpen={open(p.images)} />
            ))}
          </div>
        </div>
      </section>

      {/* Completed Projects — hidden when none are listed */}
      {PROJECTS_COMPLETED.length > 0 && (
        <>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="h-px bg-gray-200" />
          </div>

          <section id="completed" className="bg-white pt-16 md:pt-24 pb-16 md:pb-24 scroll-mt-24">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
              <div className="flex items-center gap-4 mb-4">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-navy/5 text-navy">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-navy">Delivered</span>
              </div>
              <SectionHeading
                title="Completed Projects"
                subtitle="A selection of the multifamily communities we have delivered — built to perform for decades and designed to elevate the neighborhoods they anchor across Los Angeles and Orange County."
              />
              <div className="mt-16 md:mt-20 space-y-24 md:space-y-36">
                {PROJECTS_COMPLETED.map((p, i) => (
                  <ProjectBlock key={p.title} project={p} i={i} onOpen={open(p.images)} />
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Approach */}
      <section className="bg-lightgray py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Our Approach" title="How We Deliver Every Project" />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200">
            {APPROACH.map((a, idx) => (
              <Reveal key={a.n} delay={(idx % 5) * 0.06}>
                <div className="bg-lightgray hover:bg-white transition-colors duration-300 p-8 h-full">
                  <span className="font-serif text-3xl text-gold">{a.n}</span>
                  <h3 className="mt-6 font-serif font-light text-2xl text-navy tracking-tight">{a.title}</h3>
                  <p className="mt-3 text-sm text-darkgray/60 leading-relaxed font-light">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why trust us */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Why Clients Trust Us" title="A partner you can rely on." />
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-5">
            {TRUST.map((t, i) => (
              <Reveal key={t} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
                  <span className="text-gold font-serif text-xl leading-none mt-0.5">✦</span>
                  <span className="text-darkgray font-light">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <div>
            <p className="eyebrow mb-5">Get Started</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-white tracking-tight max-w-xl leading-tight">
              Let's Build the Next Community Together
            </h2>
            <p className="mt-5 text-white/60 max-w-lg font-light leading-relaxed">
              Whether you're planning your next multifamily development or seeking a trusted construction partner, Next Horizon Builders is ready to bring your vision to life.
            </p>
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="projects-cta"
              className="group inline-flex items-center gap-3 bg-gold text-white px-9 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300 whitespace-nowrap"
            >
              Start Your Project
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {box && (
          <Lightbox images={box.images} index={box.index} onClose={close} onPrev={prev} onNext={next} />
        )}
      </AnimatePresence>
    </>
  );
}
